import { html, type Html, View } from 'rune-ts';
import klass from './SeeMoreLinkView.module.scss';
import { Title } from '../../../../atoms/Title/Title';
import { PostMessageNavigate } from '../../../../../../shared/app/navigate';
import { font } from '../../../../../../shared/typography';
import { dataStr } from '../../../../../../shared/util/dataStr';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';
import { makeCacheAvoidUrl } from '@services/MarppleShop/shared/util/url.ts';
import { AppEventHandler } from '@services/MarppleShop/shared/util/appEventListener.ts';
import { IconsBasicEnquadArrowChevronRightIcon } from '@services/MarppleShop/shared/components/atoms/Icon';

export type SeeMoreLinkViewData = {
  // default xLarge
  size?: 'xLarge' | 'large' | 'medium' | 'small' | 'xSmall';
  // default light
  theme?: keyof typeof Title.themeOptions;
  see_more_link?: string;
  navigate?: PostMessageNavigate;
  show_icon?: boolean;
};

export class SeeMoreLinkView extends View<SeeMoreLinkViewData> {
  static readonly sizeOptions = {
    xLarge: {
      see_more_font: font('16_medium'),
      size_class: klass.xLarge,
    },
    large: {
      see_more_font: font('16_medium'),
      size_class: klass.large,
    },
    medium: {
      see_more_font: font('16_medium'),
      size_class: klass.medium,
    },
    small: {
      see_more_font: font('12_medium'),
      size_class: klass.small,
    },
    xSmall: {
      see_more_font: font('12_medium'),
      size_class: klass.xSmall,
    },
  } as const;

  static readonly themeOptions = {
    light: klass.light,
    dark: klass.dark,
  } as const;

  protected override template(data: SeeMoreLinkViewData): Html {
    const { navigate, see_more_link, show_icon } = data;

    const { see_more_font, size_class } = SeeMoreLinkView.sizeOptions[this.size];
    const theme_class = SeeMoreLinkView.themeOptions[this.theme];

    return html`
      <a
        class="${klass.see_more} ${see_more_font} ${size_class} ${theme_class}"
        ${navigate ? html`data-post-message="${dataStr(navigate)}"` : ''}
        ${see_more_link ? html`href="${see_more_link}"` : ''}
      >
        ${ET(`mps2::section_title::plugin::see_more`)}
        ${show_icon ? IconsBasicEnquadArrowChevronRightIcon : null}
      </a>
    `;
  }

  protected override onRender() {
    if (MShopUtilF.isApp()) {
      AppEventHandler.delegateAppFocusEvent(({ is_focused }) => {
        const { navigate, see_more_link } = this.data;

        if (is_focused && navigate && navigate.navigate.params?.uri && see_more_link) {
          this.data.see_more_link = makeCacheAvoidUrl(see_more_link);
          navigate.navigate.params.uri = makeCacheAvoidUrl(navigate.navigate.params.uri || '');
          this.redraw();
        }
      });
    }
  }

  public get size() {
    return this.data.size || 'xLarge';
  }

  public get theme() {
    return this.data.theme || 'light';
  }

  public get seeMoreLink() {
    return this.data.see_more_link || '';
  }

  public set seeMoreLink(see_more_link: string) {
    this.data.see_more_link = see_more_link;
    this.redraw();
  }
}
